import React, { createContext, useContext } from 'react';

/*
  the context the shared pharmacy picker reads from.

  These components originally lived under the cannabis Choose Treatment page and read
  ChooseTreatmentContext directly. RX Self-Pickup needs the same picker on the shipping page, so the
  cannabis-specific dependency was replaced with this narrow context: the only things the picker
  actually needed from Choose Treatment were the selected service, the selected stock code, the
  locale, and a way to price the selected product at a given pharmacy.

  Each consumer supplies its own values:
    - cannabis Choose Treatment maps them from ChooseTreatmentContext (including per-gram pricing)
    - RX Self-Pickup supplies the selected payment model and omits pricing entirely

  Keeping this as a context rather than props avoids drilling four values through six nested
  components, and keeps each component's internals unchanged.
*/
export interface PharmacyPickerContextValue {
  /* The selected service / payment model, e.g. prescription_and_medication or prescription_only. */
  selectedService?: string;
  /* Stock code of the currently selected pharmacy, if any. */
  selectedStockCode?: string | null;
  /* Locale used for price formatting. Defaults to de-DE. */
  locale?: string;
  /*
    Price of the currently selected product at the given pharmacy's stock, or null when pricing is
    not applicable. Cannabis passes its per-stock product price; RX has a single product with one
    price and passes nothing.
  */
  getProductPriceForStock?: (stockCode: string) => number | null;
}

const PharmacyPickerContext = createContext<PharmacyPickerContextValue>({});

export const PharmacyPickerProvider: React.FC<{ value: PharmacyPickerContextValue, children: React.ReactNode }> = ({ value, children }) => (
  <PharmacyPickerContext.Provider value={value}>{children}</PharmacyPickerContext.Provider>
);

export const usePharmacyPickerContext = (): PharmacyPickerContextValue => useContext(PharmacyPickerContext);
